import { useState, type ReactNode } from "react";
import { format } from "date-fns";
import { Link, useNavigate, useParams } from "react-router";
import { ArrowLeft, CalendarDays, ChevronDown, Edit, EllipsisVertical, Eye, FileDown, MapPin, Plus, RefreshCw, Send, ShoppingCart, Trash2, UserRound } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { useUsers } from "@/features/admin/hooks/useUser";
import { usePaymentMethods } from "@/features/paymentMethods/hooks/usePaymentMethods";
import type { PaymentMethodAccount } from "@/features/paymentMethods/services/paymentMethodsApi";
import type { AuthUser } from "@/features/auth/stores/authStore";
import { useAddExpoTeamMember, useChangeExpoStatus, useExpo, useExpoCloseReadiness, useRemoveExpoTeamMember } from "../hooks/useExpos";
import { useExpoInventory, useExpoOperationalReconciliation, useExpoTransfers } from "../hooks/useExpoTransfers";
import { useExpoCashHandovers, useExpoPaymentApprovalBatches, useExpoPaymentVariances } from "../hooks/useExpoPaymentWorkflows";
import { useExpoAccountingReconciliation, useExpoSales, useRecordExpoSalePayment } from "../hooks/useExpoSales";
import {
  useExpoPerformanceReportHistory,
  useRegenerateExpoPerformanceReport,
  useResendExpoPerformanceReport,
} from "../hooks/useExpoPerformanceReports";
import { expoTransfersApi } from "../services/expoTransfersApi";
import { expoPerformanceReportsApi } from "../services/expoPerformanceReportsApi";
import type { ExpoInventoryBalance, ExpoTransfer } from "../services/expoTransfersApi";
import type { ExpoSale, RecordExpoSalePayment } from "../services/expoSalesApi";
import type { ExpoCashHandover, ExpoPaymentApprovalBatch, ExpoPaymentVariance } from "../services/expoPaymentWorkflowsApi";
import type { Expo, ExpoStatus, ExpoTeamRole } from "../services/exposApi";

const roles: ExpoTeamRole[] = ["Manager", "Seller", "Cashier", "StockCustodian", "FinanceReviewer", "Viewer"];

export function ExpoDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuthStore();
  const { data: expo, isLoading } = useExpo(id);
  const { data: users } = useUsers(1, 1000);
  const { data: transfers = [] } = useExpoTransfers(id);
  const { data: inventory = [] } = useExpoInventory(id);
  const { data: sales = [] } = useExpoSales(id);
  const { data: accounting } = useExpoAccountingReconciliation(id);
  const { data: operational } = useExpoOperationalReconciliation(id);
  const { data: paymentBatches = [] } = useExpoPaymentApprovalBatches(id);
  const { data: cashHandovers = [] } = useExpoCashHandovers(id);
  const { data: paymentVariances = [] } = useExpoPaymentVariances(id);
  const { data: closeReadiness } = useExpoCloseReadiness(id);
  const { data: paymentAccounts = [] } = usePaymentMethods({ enabled: hasPermission("expo_sales.create") });
  const statusMutation = useChangeExpoStatus();
  const addMember = useAddExpoTeamMember();
  const removeMember = useRemoveExpoTeamMember();
  const recordPayment = useRecordExpoSalePayment();
  const [userId, setUserId] = useState("");
  const [role, setRole] = useState<ExpoTeamRole>("Seller");
  const [primary, setPrimary] = useState(false);

  if (isLoading) return <p className="text-muted-foreground">Loading Expo…</p>;
  if (!expo) return <div className="space-y-4"><p>Expo not found.</p><Button onClick={() => navigate("/expos")}>Back</Button></div>;

  const transition = async (action: string, needsReason = false) => {
    const reason = needsReason ? window.prompt("Enter a reason:") : undefined;
    if (needsReason && !reason) return;
    await statusMutation.mutateAsync({ id: expo.id, action, rowVersion: expo.rowVersion, reason: reason ?? undefined });
  };

  const canEdit = hasPermission("expos.update") && ["Planned", "Approved", "Open"].includes(expo.status);
  const canRecordSale = hasPermission("expo_sales.create") && expo.status === "Active";
  const saleDisabledReason = !hasPermission("expo_sales.create")
    ? "You do not have permission to record Expo sales."
    : expo.status === "Active"
      ? ""
      : `Sales can only be recorded while the Expo is Active. Current status: ${expo.status}.`;
  const buttons: { status: ExpoStatus[]; action: string; label: string; permission: string; reason?: boolean; variant?: "default" | "outline" | "destructive" }[] = [
    { status: ["Planned"], action: "approve", label: "Approve", permission: "expos.approve" },
    { status: ["Approved"], action: "return-to-planned", label: "Return to Planned", permission: "expos.approve", reason: true, variant: "outline" },
    { status: ["Approved"], action: "open", label: "Open Operations", permission: "expos.open" },
    { status: ["Open"], action: "activate", label: "Activate", permission: "expos.activate" },
    { status: ["Active"], action: "suspend", label: "Suspend Sales", permission: "expos.activate", reason: true, variant: "outline" },
    { status: ["Active"], action: "start-closing", label: "Start closing", permission: "expos.close", reason: true, variant: "outline" },
    { status: ["Closing"], action: "close", label: "Close Expo", permission: "expos.close", reason: true },
    { status: ["Planned", "Approved", "Open"], action: "cancel", label: "Cancel Expo", permission: "expos.cancel", reason: true, variant: "destructive" },
    { status: ["Closed", "Cancelled"], action: "archive", label: "Archive", permission: "expos.archive", variant: "outline" },
  ];
  const totalExpected = transfers.filter(t => t.transferType === "ToExpo").reduce((n, t) => n + t.expectedRevenueTotal, 0);

  return <div className="space-y-6">
    <div>
      <Button variant="ghost" className="mb-2 px-0" asChild><Link to="/expos"><ArrowLeft className="mr-2 h-4 w-4" />All Expos</Link></Button>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-3"><h1 className="text-3xl font-bold">{expo.name}</h1><Badge>{expo.status}</Badge></div>
          <p className="text-muted-foreground">{expo.code}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canEdit && <Button variant="outline" asChild><Link to={`/expos/${expo.id}/edit`}><Edit className="mr-2 h-4 w-4" />Edit</Link></Button>}
          {buttons.filter(b => b.status.includes(expo.status) && hasPermission(b.permission)).map(b =>
            <Button key={b.action} variant={b.variant ?? "default"} disabled={statusMutation.isPending} onClick={() => transition(b.action, b.reason)}>{b.label}</Button>
          )}
        </div>
      </div>
    </div>

    <Tabs defaultValue="overview" className="space-y-4">
      <TabsList className="flex h-auto flex-wrap justify-start">
        <TabsTrigger value="overview">Overview</TabsTrigger>
        {hasPermission("expo_transfers.view") && <TabsTrigger value="transfers">Stock Transfers</TabsTrigger>}
        {hasPermission("expo_transfers.print") && <TabsTrigger value="notes">Transfer Notes</TabsTrigger>}
        {hasPermission("expo_sales.view") && <TabsTrigger value="sales">Sales</TabsTrigger>}
        {hasPermission("expo_sales.view") && <TabsTrigger value="payments">Payments</TabsTrigger>}
        {hasPermission("expos.view") && <TabsTrigger value="performance-reports">Performance Reports</TabsTrigger>}
        {hasPermission("expo_transfers.create") && <TabsTrigger value="returns">Returns</TabsTrigger>}
        {hasPermission("expo_inventory.view") && <TabsTrigger value="reconciliation">Reconciliation</TabsTrigger>}
      </TabsList>

      <TabsContent value="overview" className="space-y-4">
        <div className="grid gap-4 md:grid-cols-3">
          <Info icon={<CalendarDays />} label="Schedule" value={`${format(new Date(expo.startDate), "dd MMM yyyy")} – ${format(new Date(expo.endDate), "dd MMM yyyy")}`} />
          <Info icon={<MapPin />} label="Location" value={[expo.locationName, expo.town, expo.county].filter(Boolean).join(", ")} />
          <Info icon={<UserRound />} label="Manager" value={expo.managerName} />
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          <Info icon={<CalendarDays />} label="Expected value" value={money(operational?.expectedSalesValue ?? totalExpected)} />
          <Info icon={<CalendarDays />} label="Actual sales" value={money(operational?.actualSalesValue ?? sales.reduce((n, s) => n + s.totalAmount, 0))} />
          <Info icon={<CalendarDays />} label="Unsold stock" value={(operational?.unsoldStock ?? inventory.reduce((n, x) => n + x.onHandQuantity, 0)).toString()} />
        </div>
        {canRecordSale && <div className="flex justify-end"><Button asChild><Link to={`/expos/${expo.id}/sales/new`}><ShoppingCart className="mr-2 h-4 w-4" />New Expo sale</Link></Button></div>}
        <div className="rounded-lg border p-5"><h2 className="mb-2 text-lg font-semibold">Event details</h2><p className="text-sm text-muted-foreground">{expo.description || "No description provided."}</p>{expo.address && <p className="mt-3 text-sm"><span className="font-medium">Address:</span> {expo.address}</p>}<p className="mt-2 text-sm"><span className="font-medium">Timezone:</span> {expo.timezone}</p></div>
        <TeamCard expo={expo} users={users?.items ?? []} hasPermission={hasPermission} userId={userId} setUserId={setUserId} role={role} setRole={setRole} primary={primary} setPrimary={setPrimary} addMember={addMember} removeMember={removeMember} />
      </TabsContent>

      <TabsContent value="transfers" className="space-y-4">
        <div className="flex justify-end gap-2">{hasPermission("expo_transfers.create") && <><Button variant="outline" asChild><Link to={`/expos/${expo.id}/transfers/new?type=return`}>Return stock</Link></Button><Button asChild><Link to={`/expos/${expo.id}/transfers/new`}>Transfer to Expo</Link></Button></>}</div>
        <div className="grid gap-3 md:grid-cols-4"><Info icon={<CalendarDays />} label="Received" value={(operational?.totalStockTransferred ?? 0).toString()} /><Info icon={<CalendarDays />} label="On hand" value={inventory.reduce((n, x) => n + x.onHandQuantity, 0).toString()} /><Info icon={<CalendarDays />} label="Available" value={inventory.reduce((n, x) => n + x.availableQuantity, 0).toString()} /><Info icon={<CalendarDays />} label="In transit" value={transfers.flatMap(t => t.lines).reduce((n, l) => n + l.inTransitQuantity, 0).toString()} /></div>
        <TransferList transfers={transfers} expoId={expo.id} />
      </TabsContent>

      <TabsContent value="notes" className="space-y-4">
        <TransferList transfers={transfers} expoId={expo.id} showNoteActions />
      </TabsContent>

      <TabsContent value="sales" className="space-y-4">
        <SalesSummary sales={sales} inventory={inventory} expoId={expo.id} canRecordSale={canRecordSale} saleDisabledReason={saleDisabledReason} />
      </TabsContent>

      <TabsContent value="payments" className="space-y-4">
        <PaymentSummary sales={sales} accounts={paymentAccounts} batches={paymentBatches} cashHandovers={cashHandovers} variances={paymentVariances} canRecord={hasPermission("expo_sales.create") && expo.status !== "Closed" && expo.status !== "Archived" && expo.status !== "Cancelled"} recordPayment={recordPayment} />
      </TabsContent>

      <TabsContent value="performance-reports" className="space-y-4">
        <ExpoPerformanceReportsPanel expoId={expo.id} />
      </TabsContent>

      <TabsContent value="returns" className="space-y-4">
        <div className="flex justify-end">{hasPermission("expo_transfers.create") && <Button asChild><Link to={`/expos/${expo.id}/transfers/new?type=return`}>Return unsold stock</Link></Button>}</div>
        <TransferList transfers={transfers.filter(t => t.transferType === "ReturnToGreenhouse")} expoId={expo.id} showNoteActions />
      </TabsContent>

      <TabsContent value="reconciliation" className="space-y-4">
        {closeReadiness && <div className="rounded-lg border p-5"><div className="mb-3 flex items-center justify-between"><h2 className="text-lg font-semibold">Close readiness</h2><Badge variant={closeReadiness.canClose ? "default" : "secondary"}>{closeReadiness.canClose ? "Ready to close" : closeReadiness.canStartClosing ? "Can start closing" : "Not ready"}</Badge></div><div className="grid gap-3 md:grid-cols-3"><Info icon={<CalendarDays />} label="Unsold stock" value={closeReadiness.unsoldStockQuantity.toString()} /><Info icon={<CalendarDays />} label="Outstanding" value={money(closeReadiness.outstandingReceivable)} /><Info icon={<CalendarDays />} label="Open transfers" value={closeReadiness.openTransferCount.toString()} /><Info icon={<CalendarDays />} label="Pending payments" value={closeReadiness.pendingPaymentCount.toString()} /><Info icon={<CalendarDays />} label="Unreconciled payments" value={closeReadiness.unreconciledPaymentCount.toString()} /></div>{closeReadiness.blockers.length > 0 && <ul className="mt-4 list-disc space-y-1 pl-5 text-sm text-muted-foreground">{closeReadiness.blockers.map(b => <li key={b.code}>{b.message}</li>)}</ul>}</div>}
        {operational && <div className="rounded-lg border p-5"><div className="mb-3 flex items-center justify-between"><h2 className="text-lg font-semibold">Operational reconciliation</h2><Badge variant={operational.isBalanced ? "default" : "destructive"}>{operational.isBalanced ? "Balanced" : "Variance"}</Badge></div><div className="grid gap-3 md:grid-cols-3"><Info icon={<CalendarDays />} label="Transferred" value={operational.totalStockTransferred.toString()} /><Info icon={<CalendarDays />} label="Expected sales value" value={money(operational.expectedSalesValue)} /><Info icon={<CalendarDays />} label="Actual sales value" value={money(operational.actualSalesValue)} /><Info icon={<CalendarDays />} label="Collected" value={money(operational.amountCollected)} /><Info icon={<CalendarDays />} label="Outstanding" value={money(operational.outstandingBalance)} /><Info icon={<CalendarDays />} label="Returned stock" value={operational.returnedStock.toString()} /><Info icon={<CalendarDays />} label="Unsold stock" value={operational.unsoldStock.toString()} /><Info icon={<CalendarDays />} label="Damaged/lost" value={operational.damagedLostStock.toString()} /><Info icon={<CalendarDays />} label="Stock variance" value={operational.stockVariance.toString()} /></div><p className="mt-3 text-sm text-muted-foreground">Value variance: {money(operational.valueVariance)}. Damage/loss is shown as zero until the Expo adjustment workflow exists.</p></div>}
        {hasPermission("expo_sales.view") && accounting && <div className="rounded-lg border p-5"><div className="mb-3 flex items-center justify-between"><h2 className="text-lg font-semibold">Accounting reconciliation</h2><Badge variant={accounting.isReconciled ? "default" : "destructive"}>{accounting.isReconciled ? "Reconciled" : "Exceptions"}</Badge></div><div className="grid gap-3 md:grid-cols-3"><Info icon={<CalendarDays />} label="Revenue posted" value={money(accounting.revenuePosted)} /><Info icon={<CalendarDays />} label="Payments posted" value={money(accounting.settlementPosted)} /><Info icon={<CalendarDays />} label="Receivable" value={money(accounting.outstandingReceivable)} /></div>{!accounting.isReconciled && <p className="mt-3 text-sm text-destructive">Revenue variance: {accounting.revenueVariance.toFixed(2)} · Settlement variance: {accounting.settlementVariance.toFixed(2)} · Unposted documents: {accounting.unpostedSaleCount + accounting.unpostedPaymentCount}</p>}</div>}
        <div className="rounded-lg border p-5"><h2 className="mb-4 text-lg font-semibold">Lifecycle history</h2><div className="space-y-3">{expo.statusHistory.map(h => <div key={h.id} className="flex gap-3 text-sm"><div className="mt-1 h-2 w-2 rounded-full bg-primary" /><div><p><span className="font-medium">{h.toStatus}</span> by {h.changedByUserName}</p><p className="text-muted-foreground">{format(new Date(h.changedAt), "dd MMM yyyy HH:mm")}{h.reason ? ` · ${h.reason}` : ""}</p></div></div>)}</div></div>
      </TabsContent>
    </Tabs>
  </div>;
}

function ExpoPerformanceReportsPanel({ expoId }: { expoId: string }) {
  const { hasPermission } = useAuthStore();
  const { data: reports = [], isLoading, refetch, isFetching } = useExpoPerformanceReportHistory(expoId);
  const resend = useResendExpoPerformanceReport(expoId);
  const regenerate = useRegenerateExpoPerformanceReport(expoId);

  const canResend = hasPermission("expos.close");
  const canRegenerate = hasPermission("expos.close");
  const canDownload = hasPermission("reports.export");

  return <div className="rounded-lg border p-5">
    <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
      <div>
        <h2 className="text-lg font-semibold">Expo performance reports</h2>
        <p className="text-sm text-muted-foreground">Reports generated from closed Expo snapshots. Use regenerate only for authorized reopening and revision workflows.</p>
      </div>
      <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
        <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />Refresh
      </Button>
    </div>

    {isLoading && <p className="text-sm text-muted-foreground">Loading report history…</p>}

    {!isLoading && reports.length === 0 && <p className="text-sm text-muted-foreground">No performance reports yet. Reports appear after Expo closure completes and report dispatch runs.</p>}

    {reports.length > 0 && <div className="divide-y rounded-lg border">
      {reports.map(report => <div key={report.reportId} className="flex flex-wrap items-start justify-between gap-3 p-3">
        <div>
          <p className="font-medium">v{report.snapshotVersion} · revision {report.reportRevision}</p>
          <p className="text-sm text-muted-foreground">Generated {format(new Date(report.generatedAt), "dd MMM yyyy HH:mm")} · Delivery {report.deliveryStatus}</p>
          {report.failureDetails && <p className="text-sm text-destructive">{report.failureDetails}</p>}
        </div>

        <div className="flex flex-wrap gap-2">
          {canDownload && <Button size="sm" variant="outline" disabled={!report.canDownload} onClick={() => expoPerformanceReportsApi.downloadPdf(report.reportId)}>
            <FileDown className="mr-2 h-4 w-4" />PDF
          </Button>}
          {canDownload && <Button size="sm" variant="outline" disabled={!report.canDownload} onClick={() => expoPerformanceReportsApi.downloadSpreadsheet(report.reportId)}>
            <FileDown className="mr-2 h-4 w-4" />XLSX
          </Button>}
          {canResend && <Button size="sm" variant="outline" disabled={!report.canResend || resend.isPending} onClick={() => resend.mutate(report.reportId)}>
            <Send className="mr-2 h-4 w-4" />Resend
          </Button>}
          {canRegenerate && <Button
            size="sm"
            disabled={!report.canRegenerate || regenerate.isPending}
            onClick={() => {
              const reason = window.prompt("Provide authorized reason (prefix with AUTHORIZED_REOPEN:)", "AUTHORIZED_REOPEN: ");
              if (!reason) return;
              regenerate.mutate({ reportId: report.reportId, reason });
            }}
          >
            Regenerate
          </Button>}
        </div>
      </div>)}
    </div>}
  </div>;
}

function TransferList({ transfers, expoId }: { transfers: ExpoTransfer[]; expoId: string; showNoteActions?: boolean }) {
  const [openIds, setOpenIds] = useState<Record<string, boolean>>({});
  const toggle = (transferId: string) => setOpenIds((current) => ({ ...current, [transferId]: !current[transferId] }));

  return <div className="divide-y rounded-lg border">
    {transfers.map(t => {
      const isOpen = !!openIds[t.id];
      const quantity = t.lines.reduce((total, line) => total + (line.receivedQuantity || line.dispatchedQuantity || line.approvedQuantity || line.requestedQuantity), 0);

      return <Collapsible key={t.id} open={isOpen} onOpenChange={() => toggle(t.id)}>
        <div className="flex items-center justify-between gap-3 p-3">
          <button type="button" className="flex min-w-0 flex-1 items-start gap-3 text-left" onClick={() => toggle(t.id)}>
            <ChevronDown className={`mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform ${isOpen ? "rotate-180" : ""}`} />
            <span className="min-w-0">
              <span className="block truncate font-medium">{t.transferNumber}</span>
              <span className="block truncate text-xs text-muted-foreground">
                {transferTypeLabel(t.transferType)} · {format(new Date(t.transferDate), "dd MMM yyyy")} · {t.lines.length} line{t.lines.length === 1 ? "" : "s"} · {qty(quantity)} units · expected {money(t.expectedRevenueTotal)}
              </span>
            </span>
          </button>

          <div className="flex shrink-0 items-center gap-2">
            <Badge variant="secondary">{t.status}</Badge>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" size="icon" variant="ghost" aria-label={`Actions for ${t.transferNumber}`}>
                  <EllipsisVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => toggle(t.id)}>
                  <Eye className="h-4 w-4" />{isOpen ? "Hide details" : "Show details"}
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to={`/expos/${expoId}/transfers/${t.id}`}>
                    <Eye className="h-4 w-4" />Open full page
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => expoTransfersApi.downloadNote(t.id)}>
                  <FileDown className="h-4 w-4" />Download note
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <CollapsibleContent>
          <div className="border-t bg-muted/20 p-3">
            <div className="mb-3 grid gap-3 text-sm md:grid-cols-4">
              <Info icon={<CalendarDays />} label="Requested by" value={t.requestedByUserName} />
              <Info icon={<CalendarDays />} label="Requested" value={format(new Date(t.requestedAt), "dd MMM yyyy HH:mm")} />
              <Info icon={<CalendarDays />} label="Reason" value={t.reason || "Not provided"} />
              <Info icon={<CalendarDays />} label="Expected value" value={money(t.expectedRevenueTotal)} />
            </div>

            <div className="overflow-x-auto rounded-md border bg-background">
              <table className="w-full min-w-[880px] text-sm">
                <thead className="bg-muted/50 text-left">
                  <tr>
                    <th className="px-3 py-2 font-medium">Stock line</th>
                    <th className="px-3 py-2 font-medium">Source</th>
                    <th className="px-3 py-2 font-medium">Unit</th>
                    <th className="px-3 py-2 text-right font-medium">Requested</th>
                    <th className="px-3 py-2 text-right font-medium">Approved</th>
                    <th className="px-3 py-2 text-right font-medium">Dispatched</th>
                    <th className="px-3 py-2 text-right font-medium">Received</th>
                    <th className="px-3 py-2 text-right font-medium">Expected</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {t.lines.map(line => <tr key={line.id}>
                    <td className="px-3 py-2 font-medium">{line.cropTypeName}{line.variety ? <span className="block text-xs font-normal text-muted-foreground">{line.variety}</span> : null}</td>
                    <td className="px-3 py-2">{line.sourceStockLocation}</td>
                    <td className="px-3 py-2">{lineUnit(line)}</td>
                    <td className="px-3 py-2 text-right">{qty(line.requestedQuantity)}</td>
                    <td className="px-3 py-2 text-right">{qty(line.approvedQuantity)}</td>
                    <td className="px-3 py-2 text-right">{qty(line.dispatchedQuantity)}</td>
                    <td className="px-3 py-2 text-right">{qty(line.receivedQuantity)}</td>
                    <td className="px-3 py-2 text-right">{money(line.expectedRevenue)}</td>
                  </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>;
    })}
    {transfers.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No documents yet.</p>}
  </div>;
}

function SalesSummary({
  sales,
  inventory,
  expoId,
  canRecordSale,
  saleDisabledReason,
}: {
  sales: ExpoSale[];
  inventory: ExpoInventoryBalance[];
  expoId: string;
  canRecordSale: boolean;
  saleDisabledReason: string;
}) {
  const soldQuantity = sales.flatMap(s => s.items).reduce((n, item) => n + item.quantity, 0);
  const salesValue = sales.reduce((n, s) => n + s.totalAmount, 0);
  const outstanding = sales.reduce((n, s) => n + s.outstandingAmount, 0);
  const remainingQuantity = inventory.reduce((n, item) => n + item.onHandQuantity, 0);
  const soldByProduct = sales.flatMap(s => s.items).reduce<Record<string, { quantity: number; amount: number; unit: string }>>((acc, item) => {
    const key = item.productName;
    const current = acc[key] ?? { quantity: 0, amount: 0, unit: item.unit };
    current.quantity += item.quantity;
    current.amount += item.totalAmount;
    current.unit = item.unit;
    acc[key] = current;
    return acc;
  }, {});

  return <div className="space-y-4">
    <div className="rounded-lg border p-5">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Expo sales</h2>
          <p className="text-sm text-muted-foreground">Each sale below shows what was sold, quantity, unit price, total, payments, and remaining Expo stock.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="secondary">{sales.length} sale(s)</Badge>
          {canRecordSale ? (
            <Button asChild>
              <Link to={`/expos/${expoId}/sales/new`}><ShoppingCart className="mr-2 h-4 w-4" />Record Sale</Link>
            </Button>
          ) : (
            <Button disabled title={saleDisabledReason}><ShoppingCart className="mr-2 h-4 w-4" />Record Sale</Button>
          )}
        </div>
      </div>
      {!canRecordSale && saleDisabledReason && (
        <Alert className="mb-4">
          <AlertTitle>Sales unavailable</AlertTitle>
          <AlertDescription>{saleDisabledReason}</AlertDescription>
        </Alert>
      )}
      <div className="mb-4 grid gap-3 md:grid-cols-4">
        <Info icon={<CalendarDays />} label="Sales value" value={money(salesValue)} />
        <Info icon={<CalendarDays />} label="Quantity sold" value={qty(soldQuantity)} />
        <Info icon={<CalendarDays />} label="Outstanding" value={money(outstanding)} />
        <Info icon={<CalendarDays />} label="Remaining stock" value={qty(remainingQuantity)} />
      </div>
      {Object.keys(soldByProduct).length > 0 && <div className="mb-4 rounded-lg border bg-muted/20 p-4">
        <h3 className="mb-3 text-sm font-semibold">Sold by item</h3>
        <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
          {Object.entries(soldByProduct).map(([name, row]) => <div key={name} className="rounded-md bg-background p-3 text-sm">
            <p className="font-medium">{name}</p>
            <p className="text-muted-foreground">{qty(row.quantity)} {row.unit} · {money(row.amount)}</p>
          </div>)}
        </div>
      </div>}
    </div>

    <div className="space-y-3">
      {sales.length === 0 && (
        <div className="rounded-lg border p-8 text-center">
          <h3 className="text-lg font-semibold">No Expo sales recorded</h3>
          <p className="mx-auto mt-2 max-w-2xl text-sm text-muted-foreground">
            Record sales against stock that has been transferred to and received by this Expo.
          </p>
          {inventory.length === 0 || remainingQuantity <= 0 ? (
            <Alert className="mx-auto mt-4 max-w-2xl text-left">
              <AlertTitle>No received Expo stock</AlertTitle>
              <AlertDescription>Receive Expo stock before recording a sale so the form can allocate quantities from this Expo inventory.</AlertDescription>
            </Alert>
          ) : canRecordSale ? (
            <Button className="mt-4" asChild>
              <Link to={`/expos/${expoId}/sales/new`}><ShoppingCart className="mr-2 h-4 w-4" />Record First Sale</Link>
            </Button>
          ) : (
            <p className="mt-4 text-sm text-muted-foreground">{saleDisabledReason}</p>
          )}
        </div>
      )}
      {sales.map(sale => {
        const paid = sale.recordedPaymentAmount;
        return <div key={sale.id} className="rounded-lg border bg-background p-4">
          <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="font-semibold">{sale.orderNumber}</p>
              <p className="text-sm text-muted-foreground">{format(new Date(sale.orderDate), "dd MMM yyyy HH:mm")}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={sale.outstandingAmount > 0 ? "secondary" : "default"}>{sale.paymentStatus}</Badge>
              {sale.creditApproved && <Badge variant="outline">Credit approved</Badge>}
            </div>
          </div>

          <div className="overflow-x-auto rounded-md border">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="bg-muted/50 text-left">
                <tr>
                  <th className="px-3 py-2 font-medium">Item sold</th>
                  <th className="px-3 py-2 text-right font-medium">Quantity</th>
                  <th className="px-3 py-2 font-medium">Unit</th>
                  <th className="px-3 py-2 text-right font-medium">Unit price</th>
                  <th className="px-3 py-2 text-right font-medium">Line total</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {sale.items.map(item => <tr key={item.id}>
                  <td className="px-3 py-2 font-medium">{item.productName}</td>
                  <td className="px-3 py-2 text-right">{qty(item.quantity)}</td>
                  <td className="px-3 py-2">{item.unit}</td>
                  <td className="px-3 py-2 text-right">{money(item.unitPrice)}</td>
                  <td className="px-3 py-2 text-right font-medium">{money(item.totalAmount)}</td>
                </tr>)}
              </tbody>
            </table>
          </div>

          <div className="mt-3 grid gap-3 md:grid-cols-3">
            <div className="rounded-md bg-muted/30 p-3 text-sm"><span className="text-muted-foreground">Sale total</span><p className="font-semibold">{money(sale.totalAmount)}</p></div>
            <div className="rounded-md bg-muted/30 p-3 text-sm"><span className="text-muted-foreground">Recorded payments</span><p className="font-semibold">{money(paid)}</p></div>
            <div className="rounded-md bg-muted/30 p-3 text-sm"><span className="text-muted-foreground">Outstanding</span><p className="font-semibold">{money(sale.outstandingAmount)}</p></div>
          </div>
          {sale.payments.length > 0 && <div className="mt-3 divide-y rounded-md border">
            {sale.payments.map(payment => <div key={payment.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
              <span>{payment.paymentNumber} · {payment.method}{payment.reference ? ` · ${payment.reference}` : ""}</span>
              <span>{money(payment.amount)} · {payment.approvalStatus}{payment.isReconciled ? " · reconciled" : ""}</span>
            </div>)}
          </div>}
        </div>;
      })}
    </div>

    <div className="rounded-lg border p-5">
      <h3 className="mb-3 text-lg font-semibold">Remaining Expo stock</h3>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="px-3 py-2 font-medium">Item</th>
              <th className="px-3 py-2 font-medium">Unit</th>
              <th className="px-3 py-2 text-right font-medium">Received</th>
              <th className="px-3 py-2 text-right font-medium">Sold</th>
              <th className="px-3 py-2 text-right font-medium">Returned</th>
              <th className="px-3 py-2 text-right font-medium">On hand</th>
              <th className="px-3 py-2 text-right font-medium">Available</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {inventory.map(item => <tr key={item.id}>
              <td className="px-3 py-2 font-medium">{item.cropTypeName}{item.variety ? ` · ${item.variety}` : ""}{item.trayType ? <span className="block text-xs font-normal text-muted-foreground">{item.trayType}</span> : null}</td>
              <td className="px-3 py-2">{item.stockUnit}</td>
              <td className="px-3 py-2 text-right">{stockQty(item.totalReceivedQuantity, item.receivedBaseQuantity, item.trayCapacity, item.stockUnit)}</td>
              <td className="px-3 py-2 text-right">{stockQty(item.totalSoldQuantity, item.soldBaseQuantity, item.trayCapacity, item.stockUnit)}</td>
              <td className="px-3 py-2 text-right">{stockQty(item.totalReturnedQuantity, item.returnedBaseQuantity, item.trayCapacity, item.stockUnit)}</td>
              <td className="px-3 py-2 text-right font-medium">{stockQty(item.onHandQuantity, item.onHandBaseQuantity, item.trayCapacity, item.stockUnit)}</td>
              <td className="px-3 py-2 text-right">{stockQty(item.availableQuantity, item.availableBaseQuantity, item.trayCapacity, item.stockUnit)}</td>
            </tr>)}
          </tbody>
        </table>
      </div>
      {inventory.length === 0 && <p className="mt-3 text-sm text-muted-foreground">No Expo inventory has been received yet.</p>}
    </div>
  </div>;
}

function PaymentSummary({
  sales,
  accounts,
  batches,
  cashHandovers,
  variances,
  canRecord,
  recordPayment,
}: {
  sales: ExpoSale[];
  accounts: PaymentMethodAccount[];
  batches: ExpoPaymentApprovalBatch[];
  cashHandovers: ExpoCashHandover[];
  variances: ExpoPaymentVariance[];
  canRecord: boolean;
  recordPayment: {
    isPending: boolean;
    mutateAsync: (value: { saleId: string; data: RecordExpoSalePayment }) => Promise<unknown>;
  };
}) {
  const payableSales = sales.filter(s => s.outstandingAmount > 0);
  const [saleId, setSaleId] = useState("");
  const [accountId, setAccountId] = useState("");
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const payments = sales.flatMap(s => s.payments.map(p => ({ ...p, orderNumber: s.orderNumber })));
  const approvedAmount = payments.filter(p => p.approvalStatus === "Approved").reduce((n, p) => n + p.amount, 0);
  const pendingAmount = payments.filter(p => p.approvalStatus === "PendingApproval").reduce((n, p) => n + p.amount, 0);
  const channelTotals = batches.reduce<Record<string, number>>((acc, batch) => {
    acc[batch.paymentChannel] = (acc[batch.paymentChannel] ?? 0) + batch.reportedTotal;
    return acc;
  }, {});
  const activeBatches = batches.filter(b => !["Approved", "Rejected", "Cancelled"].includes(b.status));
  const activeHandovers = cashHandovers.filter(h => !["Reconciled", "Cancelled"].includes(h.status));
  const openVariances = variances.filter(v => !v.isResolved);
  const selectedSale = sales.find(s => s.id === saleId);
  const parsedAmount = Number(amount);
  const canSubmit = canRecord && saleId && accountId && parsedAmount > 0 && (!selectedSale || parsedAmount <= selectedSale.outstandingAmount + 0.01);
  const submit = async () => {
    if (!canSubmit) return;
    await recordPayment.mutateAsync({ saleId, data: { paymentMethodAccountId: accountId, amount: parsedAmount, paymentDate: new Date().toISOString(), reference: reference || undefined, notes: notes || undefined } });
    setSaleId(""); setAccountId(""); setAmount(""); setReference(""); setNotes("");
  };
  return <div className="space-y-4">
    <div className="rounded-lg border p-5">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Payments</h2>
          <p className="text-sm text-muted-foreground">
            Cash, M-Pesa, and bank receipts are all Expo payments. Only physical cash goes through cash handover; M-Pesa and bank are verified from approval batches, references, and reconciliation evidence.
          </p>
        </div>
        <Badge variant="secondary">{payableSales.length} unpaid sale(s)</Badge>
      </div>
      {canRecord && payableSales.length > 0 && <div className="mb-5 grid gap-2 rounded-md bg-muted/40 p-3 md:grid-cols-[1.4fr_1fr_120px_1fr_1fr_auto]"><select className="h-9 rounded-md border bg-background px-3 text-sm" value={saleId} onChange={e => { const next = e.target.value; setSaleId(next); const sale = sales.find(s => s.id === next); setAmount(sale ? sale.outstandingAmount.toString() : ""); }}><option value="">Select sale</option>{payableSales.map(s => <option key={s.id} value={s.id}>{s.orderNumber} · outstanding {money(s.outstandingAmount)}</option>)}</select><select className="h-9 rounded-md border bg-background px-3 text-sm" value={accountId} onChange={e => setAccountId(e.target.value)}><option value="">Payment method</option>{accounts.map(a => <option key={a.id} value={a.id}>{a.name} · {a.typeName}</option>)}</select><input className="h-9 rounded-md border bg-background px-3 text-sm" type="number" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Amount" /><input className="h-9 rounded-md border bg-background px-3 text-sm" value={reference} onChange={e => setReference(e.target.value)} placeholder="M-Pesa/bank/cash reference" /><input className="h-9 rounded-md border bg-background px-3 text-sm" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes" /><Button disabled={!canSubmit || recordPayment.isPending} onClick={submit}>Record</Button></div>}
      {selectedSale && parsedAmount > selectedSale.outstandingAmount + 0.01 && <p className="mb-3 text-sm text-destructive">Amount cannot exceed this sale's outstanding balance.</p>}
      <div className="mb-3 grid gap-3 md:grid-cols-4"><Info icon={<CalendarDays />} label="Recorded" value={money(payments.reduce((n, p) => n + p.amount, 0))} /><Info icon={<CalendarDays />} label="Pending approval" value={money(pendingAmount)} /><Info icon={<CalendarDays />} label="Approved" value={money(approvedAmount)} /><Info icon={<CalendarDays />} label="Reconciled items" value={payments.filter(p => p.isReconciled).length.toString()} /></div>
      {payments.map(p => <div key={p.id} className="flex justify-between border-t py-2 text-sm"><span>{p.paymentNumber} · {p.orderNumber}</span><span>{money(p.amount)} · {p.method} · {p.approvalStatus}{p.isReconciled ? " · reconciled" : ""}</span></div>)}
      {payments.length === 0 && <p className="text-sm text-muted-foreground">No payments recorded for this Expo.</p>}
    </div>

    <div className="grid gap-4 lg:grid-cols-3">
      <div className="rounded-lg border p-5">
        <h3 className="font-semibold">Payment approval batches</h3>
        <p className="mt-1 text-sm text-muted-foreground">Use for cash, M-Pesa, and bank payments. Finance confirms the amount that should settle receivables.</p>
        <div className="mt-4 grid gap-2 text-sm">
          {Object.entries(channelTotals).map(([channel, total]) => <div key={channel} className="flex justify-between"><span>{channelLabel(channel)}</span><span>{money(total)}</span></div>)}
          {Object.keys(channelTotals).length === 0 && <p className="text-muted-foreground">No approval batches yet.</p>}
        </div>
        <div className="mt-4 border-t pt-3 text-sm"><span className="font-medium">{activeBatches.length}</span> active batch(es)</div>
      </div>

      <div className="rounded-lg border p-5">
        <h3 className="font-semibold">Physical cash handovers</h3>
        <p className="mt-1 text-sm text-muted-foreground">Only notes and coins collected at the Expo should appear here. M-Pesa and bank deposits should not be handed over as cash.</p>
        <div className="mt-4 grid gap-2 text-sm">
          <div className="flex justify-between"><span>Expected cash</span><span>{money(cashHandovers.reduce((n, h) => n + h.expectedAmount, 0))}</span></div>
          <div className="flex justify-between"><span>Received cash</span><span>{money(cashHandovers.reduce((n, h) => n + (h.receivedAmount ?? 0), 0))}</span></div>
          <div className="flex justify-between"><span>Cash variance</span><span>{money(cashHandovers.reduce((n, h) => n + (h.varianceAmount ?? 0), 0))}</span></div>
        </div>
        <div className="mt-4 border-t pt-3 text-sm"><span className="font-medium">{activeHandovers.length}</span> open handover(s)</div>
      </div>

      <div className="rounded-lg border p-5">
        <h3 className="font-semibold">Variance review</h3>
        <p className="mt-1 text-sm text-muted-foreground">Differences are not discarded. Short receipts, over-declarations, rejected claims, and evidence issues stay visible until resolved.</p>
        <div className="mt-4 text-sm"><span className="font-medium">{openVariances.length}</span> unresolved variance(s)</div>
        <div className="mt-3 space-y-2">
          {openVariances.slice(0, 4).map(v => <div key={`${v.transactionType}-${v.transactionId}-${v.itemId ?? "row"}`} className="rounded-md bg-muted/40 p-2 text-xs"><div className="flex justify-between gap-2"><span>{v.transactionNumber} · {channelLabel(v.paymentChannel)}</span><span>{money(v.varianceAmount)}</span></div><p className="text-muted-foreground">{v.reason}</p></div>)}
        </div>
      </div>
    </div>
  </div>;
}

interface TeamCardProps {
  expo: Expo;
  users: AuthUser[];
  hasPermission: (permission: string) => boolean;
  userId: string;
  setUserId: (value: string) => void;
  role: ExpoTeamRole;
  setRole: (value: ExpoTeamRole) => void;
  primary: boolean;
  setPrimary: (value: boolean) => void;
  addMember: { isPending: boolean; mutateAsync: (value: { id: string; data: { userId: string; teamRole: ExpoTeamRole; effectiveFrom: string; isPrimary: boolean } }) => Promise<unknown> };
  removeMember: { mutate: (value: { id: string; memberId: string }) => void };
}

function TeamCard({ expo, users, hasPermission, userId, setUserId, role, setRole, primary, setPrimary, addMember, removeMember }: TeamCardProps) {
  return <div className="rounded-lg border p-5"><div className="mb-4 flex items-center justify-between"><h2 className="text-lg font-semibold">Expo team</h2><Badge variant="secondary">{expo.teamMembers.length} assigned</Badge></div>{hasPermission("expo_team.manage") && <div className="mb-5 grid gap-2 rounded-md bg-muted/40 p-3 md:grid-cols-[1fr_180px_auto_auto]"><select className="h-9 rounded-md border bg-background px-3 text-sm" value={userId} onChange={e => setUserId(e.target.value)}><option value="">Select user</option>{users.filter(u => u.isActive).map(u => <option key={u.id} value={u.id}>{u.firstName} {u.otherNames}</option>)}</select><select className="h-9 rounded-md border bg-background px-3 text-sm" value={role} onChange={e => setRole(e.target.value as ExpoTeamRole)}>{roles.map(r => <option key={r}>{r}</option>)}</select><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={primary} onChange={e => setPrimary(e.target.checked)} />Primary</label><Button disabled={!userId || addMember.isPending} onClick={async () => { await addMember.mutateAsync({ id: expo.id, data: { userId, teamRole: role, effectiveFrom: new Date().toISOString(), isPrimary: primary } }); setUserId(""); setPrimary(false); }}><Plus className="mr-2 h-4 w-4" />Add</Button></div>}<div className="divide-y">{expo.teamMembers.map(m => <div className="flex items-center justify-between py-3" key={m.id}><div><p className="font-medium">{m.userName}{m.isPrimary && <Badge className="ml-2" variant="outline">Primary</Badge>}</p><p className="text-sm text-muted-foreground">{m.teamRole} · from {format(new Date(m.effectiveFrom), "dd MMM yyyy")}</p></div>{hasPermission("expo_team.manage") && <Button size="icon" variant="ghost" onClick={() => removeMember.mutate({ id: expo.id, memberId: m.id })}><Trash2 className="h-4 w-4" /></Button>}</div>)}{expo.teamMembers.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">No additional team members assigned.</p>}</div></div>;
}

function Info({ icon, label, value }: { icon: ReactNode; label: string; value: string }) { return <div className="flex gap-3 rounded-lg border p-4"><div className="mt-1 [&>svg]:h-5 [&>svg]:w-5 [&>svg]:text-muted-foreground">{icon}</div><div><p className="text-xs uppercase text-muted-foreground">{label}</p><p className="font-medium">{value}</p></div></div>; }
function money(value: number) { return `KES ${value.toFixed(2)}`; }
function qty(value: number) { return Number.isInteger(value) ? value.toString() : value.toFixed(2); }
function transferTypeLabel(type: string) {
  if (type === "ToExpo") return "To Expo";
  if (type === "ReturnToGreenhouse") return "Return to greenhouse";
  return type;
}
function lineUnit(line: ExpoTransfer["lines"][number]) {
  const tray = line.trayType?.toLowerCase() ?? "";
  if (tray.includes("210") || tray.includes("twoten") || tray.includes("two ten")) return "210-hole tray";
  if (tray.includes("442") || tray.includes("fourfortytwo") || tray.includes("four forty two")) return "442-hole tray";
  return line.trayType || line.stockUnit || "Units";
}
function stockQty(trayQuantity: number, baseQuantity?: number | null, trayCapacity?: number | null, unit?: string | null) {
  if (!trayCapacity) return `${qty(trayQuantity)} ${unit ?? ""}`.trim();
  const exactBase = Math.round(baseQuantity ?? trayQuantity * trayCapacity);
  const completeTrays = Math.floor(exactBase / trayCapacity);
  const loosePieces = exactBase % trayCapacity;
  const trayLabel = completeTrays === 1 ? "tray" : "trays";

  if (completeTrays > 0 && loosePieces > 0)
    return `${completeTrays} ${trayLabel} + ${loosePieces.toLocaleString()} pieces (${exactBase.toLocaleString()} pieces)`;

  if (completeTrays > 0)
    return `${completeTrays} ${trayLabel} (${exactBase.toLocaleString()} pieces)`;

  return `${exactBase.toLocaleString()} pieces`;
}
function channelLabel(channel?: string | null) {
  if (channel === "MobileMoney") return "M-Pesa / mobile money";
  if (channel === "Bank") return "Bank / card / cheque";
  if (channel === "Cash") return "Physical cash";
  return channel || "Other";
}
